import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Subject } from 'rxjs';
import { Store, ActionsSubject, select } from '@ngrx/store';
import { MmPopoverService } from '~shared/modules/popover/services/popover.service';
import { ofType } from '@ngrx/effects';
import {
  BuyerState,
  HideSuggestionsList,
  isSearchInputFocused,
  SearchSuggestionsActionTypes
} from '~app/modules/generic/store';
import { take, takeUntil, filter, delay } from 'rxjs/operators';
import { ScrollStrategies } from '~shared/modules/popover/models/popover-scroll-strategy.enum';
import {
  SuggestionsListComponent
} from '~app/modules/generic/components/header/suggestions-list/suggestions-list.component';
import { MediaObserver } from '@angular/flex-layout';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { NUMBER_0, NUMBER_200 } from '~shared/constants';

@Injectable({
  providedIn: 'root'
})
export class SuggestionsPopoverService implements OnDestroy {
  destroySubject = new Subject<boolean>();
  private searchPopoverRef: any;
  private isMobile: boolean;
  private isSearchInputFocused: boolean;
  private origin: HTMLFormElement;
  private windowClickHandlerBoundThis = null;

  constructor(
    private readonly store: Store<BuyerState>,
    private readonly popoverSvc: MmPopoverService,
    private readonly actions$: ActionsSubject,
    private readonly mediaObserver: MediaObserver,
    @Inject(PLATFORM_ID) private platform: Object
  ) {
    this.isMobile = this.mediaObserver.isActive(MEDIA_ALISES.mobile);

    // TODO, try to find better solution
    // TODO We are using it now just to not show suggestions unless the input is focused
    this.store
      .pipe(
        select(isSearchInputFocused),
        takeUntil(this.destroySubject)
      )
      .subscribe(isFocused => this.isSearchInputFocused = isFocused);

    this.closeSearchOnHideSearch();
    this.closeSearchOnOutsideClick();
    this.showSearchOnShowSearch();
  }

  ngOnDestroy() {
    this.destroySubject.next();
    window.removeEventListener('click', this.windowClickHandlerBoundThis);
  }

  initHeaderSearch(origin) {
    this.origin = origin;
  }

  private showSearchOnShowSearch() {
    this.actions$.pipe(
      ofType(SearchSuggestionsActionTypes.SHOW_SUGGESTIONS_LIST),
      filter(_ => !!this.origin),
      delay(NUMBER_200),
      takeUntil(this.destroySubject),
    ).subscribe(() => this.showSearchPopover());
  }

  private closeSearchOnOutsideClick() {
    if (isPlatformServer(this.platform)) {
      return;
    }

    this.windowClickHandlerBoundThis = this.windowClickHandler.bind(this);
    window.addEventListener('click', this.windowClickHandlerBoundThis);
  }

  private closeSearchOnHideSearch() {
    this.actions$.pipe(
      ofType(SearchSuggestionsActionTypes.HIDE_SUGGESTIONS_LIST),
      takeUntil(this.destroySubject)
    ).subscribe(() => this.closeSearchPopover());
  }

  private createSearchPopover() {
    const { width } = this.origin.getBoundingClientRect();

    this.searchPopoverRef = this.popoverSvc.open({
      origin: this.origin,
      content: SuggestionsListComponent,
      width,
      offsetY: -2,
      scrollStrategy: ScrollStrategies.block,
      hasBackdrop: false,
      data: {
        isMobile: false
      }
    });

    this.searchPopoverRef.afterClosed$.pipe(take(1))
      .subscribe(() => {
        this.searchPopoverRef = undefined;
      });
  }

  private showSearchPopover() {
    if (this.searchPopoverRef || !this.isSearchInputFocused) {
      return;
    }

    this.createSearchPopover();
  }

  private closeSearchPopover() {
    if (this.searchPopoverRef) {
      this.searchPopoverRef.close();
    }
  }

  private windowClickHandler({ target }) {
    if (this.searchPopoverRef) {
      const eventTarget = target as Node;
      const isSearchFieldClicked = this.origin.contains(eventTarget);
      const isSearchDropdownClicked = this.searchPopoverRef.overlay.overlayElement.contains(eventTarget);
      const isClickedOutside = !(isSearchFieldClicked || isSearchDropdownClicked);

      if (isClickedOutside) {
        this.store.dispatch(new HideSuggestionsList());
      }
    }
  }

}
